import React, { useEffect, useRef, FC, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

// PostHog is loaded dynamically to reduce initial bundle size
let posthogInstance: any = null;
let initPromise: Promise<any> | null = null;

// Deferred initialization - only loads PostHog after user interaction or timeout
const initPostHog = async (): Promise<any> => {
  if (posthogInstance) return posthogInstance;
  if (initPromise) return initPromise;

  initPromise = import('posthog-js').then(({ default: posthog }) => {
    posthog.init(import.meta.env.VITE_POSTHOG_KEY || '', {
      api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
      autocapture: true,
      capture_pageview: false, // We handle page views manually
      disable_session_recording: false,
      loaded: () => {
        posthogInstance = posthog;
      }
    });
    posthogInstance = posthog;
    return posthog;
  });

  return initPromise;
};

// Queue for events before PostHog is loaded
const eventQueue: Array<{ type: 'pageview' | 'identify' | 'reset' | 'capture'; data?: any }> = [];

const flushQueue = async () => {
  const ph = await initPostHog();
  while (eventQueue.length > 0) {
    const event = eventQueue.shift();
    if (!event) continue;

    switch (event.type) {
      case 'pageview':
        ph.capture('$pageview');
        break;
      case 'identify':
        ph.identify(event.data.id, event.data.properties);
        break;
      case 'reset':
        ph.reset();
        break;
      case 'capture':
        ph.capture(event.data.event, event.data.properties);
        break;
    }
  }
};

// Public API that queues if not ready
export const posthog = {
  capture: (event: string, properties?: Record<string, any>) => {
    if (posthogInstance) {
      posthogInstance.capture(event, properties);
    } else {
      eventQueue.push({ type: 'capture', data: { event, properties } });
    }
  },
  identify: (id: string, properties?: Record<string, any>) => {
    if (posthogInstance) {
      posthogInstance.identify(id, properties);
    } else {
      eventQueue.push({ type: 'identify', data: { id, properties } });
    }
  },
  reset: () => {
    if (posthogInstance) {
      posthogInstance.reset();
    } else {
      eventQueue.push({ type: 'reset' });
    }
  }
};

export const PostHogProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const hasInitialized = useRef(false);

  // Initialize PostHog after first user interaction or after 3 seconds
  useEffect(() => {
    if (hasInitialized.current) return;

    const initOnInteraction = () => {
      if (!hasInitialized.current) {
        hasInitialized.current = true;
        initPostHog().then(flushQueue);
        cleanup();
      }
    };

    // Start loading after 3 seconds if no interaction
    const timeout = setTimeout(initOnInteraction, 3000);

    // Or load immediately on first interaction
    const events = ['click', 'scroll', 'keydown', 'touchstart'];
    events.forEach(e => window.addEventListener(e, initOnInteraction, { once: true, passive: true }));

    const cleanup = () => {
      clearTimeout(timeout);
      events.forEach(e => window.removeEventListener(e, initOnInteraction));
    };

    return cleanup;
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (posthogInstance) {
      posthogInstance.capture('$pageview');
    } else {
      eventQueue.push({ type: 'pageview' });
    }
  }, [location.pathname]);

  return <>{children}</>;
};

// Hook to identify user
export const usePostHogIdentify = (user: { id: string; email?: string; name: string } | null) => {
  useEffect(() => {
    if (user) {
      posthog.identify(user.id, {
        email: user.email,
        name: user.name,
      });
    } else {
      posthog.reset();
    }
  }, [user?.id, user?.email, user?.name]);
};
